import { Component, DestroyRef, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CustomerModel } from '@libs/account/models';
import { AuthService } from '@libs/shared/auth';
import { AppStateService, CapacitorService, EnvironmentService } from '@libs/shared/common';
import { LiveUpdateService } from '@libs/shared/live-update';
import { GrafanaFaroService } from '@libs/shared/logging';
import { AppleAppTrackingService, ConsentManagerService, ShopRequestErrorHandlerService } from '@libs/shared/services';
import { PageInfoTrackingService } from '@libs/shared/tracking';
import { GtmService } from '@libs/shared/ui';
import { distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'cep-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit {
  public readonly rootId: string = '2800';
  public showSplashAnimation: boolean = true;

  public constructor(
    private readonly env: EnvironmentService,
    private readonly state: AppStateService,
    private readonly authService: AuthService,
    private readonly consentService: ConsentManagerService,
    private readonly appleAppTrackingService: AppleAppTrackingService,
    private readonly gtmService: GtmService,
    private readonly liveUpdateService: LiveUpdateService,
    private readonly destroyRef: DestroyRef,
    private readonly faroService: GrafanaFaroService,
    private readonly pageInfoTrackingService: PageInfoTrackingService,
    private readonly capacitorService: CapacitorService,
    private readonly shopRequestErrorHandlerService: ShopRequestErrorHandlerService,
  ) {
    void this.liveUpdateService.activateCurrentVersion();
    this.shopRequestErrorHandlerService.init();
  }

  public ngOnInit(): void {
    this.faroService.init('lekkerland-cep-frontend');

    // must subscribe before the initial NavigationEnd, so it cannot wait for the splash-gated initializeTracking()
    this.pageInfoTrackingService.init();

    this.showSplashAnimation = this.env.type() !== 'local' && this.capacitorService.isCapacitorApp();

    if (!this.showSplashAnimation) {
      this.initializeTracking();
    }

    this.state.set('hidePageNavigation', false);
    this.authService.getCurrentCustomer()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged((prev?: CustomerModel, current?: CustomerModel) => {
          return prev?.id === current?.id;
        }))
      .subscribe(this.updatePageState.bind(this));
  }

  public onSplashScreenAnimationDone(): void {
    this.showSplashAnimation = false;
    this.initializeTracking();
  }

  private initializeTracking(): void {
    void this.appleAppTrackingService.requestAppleAppTracking();
    this.consentService.initialize();
    this.gtmService.initialize();
  }

  private updatePageState(customer?: CustomerModel): void {
    this.state.set('page', {
      rootId: this.rootId,
      customerNumber: customer?.id,
      useAppMainNavigation: true,
    });
  }
}
